import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
// import { useState } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { makeEncryptedApiCall } from "../utils/encryptionUtils";

interface EditUserProps {
  editUserIdData: any;
  onCloseDrawer?: () => void;
  onUserUpdate?: () => void;
}

const EditUser = ({ editUserIdData, onCloseDrawer, onUserUpdate }: EditUserProps) => {
  console.log(editUserIdData, "editUserIdData");

  const validation_schema = Yup.object().shape({
    firstname: Yup.string().required("First name is required"),
    lastname: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    contact: Yup.string()
      .matches(/^\d{10}$/, "Contact must be 10 digits")
      .required("Contact is required"),
  });

  const mapPermissions = (permissions: string[] = []) => {
    if (
      permissions.includes("Invite Organizations") &&
      permissions.includes("Modify Licenses")
    ) {
      return "invite_and_modify";
    } else if (permissions.includes("Invite Organizations")) {
      return "invite_only";
    } else {
      return "read_only";
    }
  };

  const formik = useFormik({
    initialValues: {
      firstname: editUserIdData?.first_name || "",
      lastname: editUserIdData?.last_name || "",
      email: editUserIdData?.email || "",
      contact: editUserIdData?.contact || "",
      permission: mapPermissions(editUserIdData?.permissions || []),
    },
    validationSchema: validation_schema,
    onSubmit: async (values) => {
      try {
        const payload = {
          user_id: editUserIdData?.id,
          first_name: (values.firstname || "").trim(),
          last_name: (values.lastname || "").trim(),
          email: (values.email || "").trim(),
          phone_number: (values.contact || "").trim(),
          role: (values.permission || "").trim(),
        };

        console.log("=== PAYLOAD BEING SENT (ENCRYPTED) ===");
        console.log("Raw payload:", JSON.stringify(payload, null, 2));
        console.log("Payload keys:", Object.keys(payload));
        console.log("========================");

        // Use encrypted API call utility with Bearer token
        const response = await makeEncryptedApiCall(
          "/authentication/sa-edit-user",
          payload
        );
        console.log("API Response:", response.data);
        toast.success(response?.data?.message);
        if (onUserUpdate) {
          onUserUpdate();
        } else if (onCloseDrawer) {
          onCloseDrawer();
        }
        // Navigate to organization table after successful registration
        // navigate("/organisation-table");
        //  formik.resetForm();
        //  InviteTablerefetch();
      } catch (error: any) {
        console.error("Error registering organization:", error);
        // Handle error (show toast notification, etc.)
        toast.error(error?.response?.data?.message);
      }
    },
  });

  const roleMap = {
    read_only: "read_only", // View Organisations
    invite_only: "invite_only", // Invite Organizations
    invite_and_modify: "invite_and_modify", // Modify Licenses
  };

  const options = [
    { value: "read_only", label: "View Organizations" },
    { value: "invite_only", label: "Invite Organization" },
    { value: "invite_and_modify", label: "Modify Licenses" },
  ];

  return (
    <div style={{ padding: 20 }}>
      <form onSubmit={formik.handleSubmit} style={{ flexGrow: 1 }}>
        <Box display="flex" gap={2} className="mb_3">
          <TextField
            sx={{ mt: 2 }}
            id="First Name"
            label="First Name"
            variant="outlined"
            required
            fullWidth
            name="firstname"
            InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.firstname}
            error={formik.touched.firstname && Boolean(formik.errors.firstname)}
            helperText={formik.touched.firstname && formik.errors.firstname}
            inputProps={{ maxLength: 64 }}
          />
          <TextField
            sx={{ mt: 2 }}
            id="Last Name"
            label="Last Name"
            variant="outlined"
            required
            fullWidth
            name="lastname"
            InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.lastname}
            error={formik.touched.lastname && Boolean(formik.errors.lastname)}
            helperText={formik.touched.lastname && formik.errors.lastname}
            inputProps={{ maxLength: 64 }}
          />
        </Box>
        <Box display="flex" gap={2} className="mb_3">
          <TextField
            sx={{ width: "49%" }}
            id="email"
            label="Email"
            variant="outlined"
            required
            fullWidth
            name="email"
            InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            helperText={formik.touched.email && formik.errors.email}
            error={formik.touched.email && Boolean(formik.errors.email)}
            inputProps={{ maxLength: 64 }}
          />
          <TextField
            sx={{ width: "49%" }}
            id="contact"
            label="Contact"
            variant="outlined"
            required
            fullWidth
            name="contact"
            InputLabelProps={{ sx: { fontWeight: 700, color: "black" } }}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              formik.setFieldValue("contact", value);
            }}
            onBlur={formik.handleBlur}
            helperText={formik.touched.contact && formik.errors.contact}
            error={formik.touched.contact && Boolean(formik.errors.contact)}
            value={formik.values.contact}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 10, // for example, 10 digits max
            }}
          />
        </Box>
        <Typography variant="body2" fontWeight="bold" marginTop={2}>
          Permission
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          {options.map((option) => {
            const isChecked =
              option.value === "read_only" ||
              (option.value === "invite_only" &&
                (formik.values.permission === "invite_only" ||
                  formik.values.permission === "invite_and_modify")) ||
              (option.value === "invite_and_modify" &&
                formik.values.permission === "invite_and_modify");

            return (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={isChecked}
                    disabled={option.value === "read_only"} // Always checked
                    onChange={() => {
                      const current = formik.values.permission;

                      if (option.value === "invite_and_modify") {
                        if (isChecked) {
                          // If Invite Organizations is already selected, keep it
                          formik.setFieldValue(
                            "permission",
                            current === "invite_and_modify" &&
                              current !== "invite_only"
                              ? "invite_only"
                              : current
                          );
                        } else {
                          // Turn on modify
                          formik.setFieldValue(
                            "permission",
                            "invite_and_modify"
                          );
                        }
                      } else if (option.value === "invite_only") {
                        formik.setFieldValue(
                          "permission",
                          isChecked ? "read_only" : "invite_only"
                        );
                      }
                    }}
                  />
                }
                label={option.label}
              />
            );
          })}
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="small"
            sx={{
              minWidth: 100,
              minHeight: 35,
              position: "fixed",
              right: 32,
              bottom: 32,
              zIndex: 10,
            }}
          >
            Update
          </Button>
        </Box>
      </form>
    </div>
  );
};

export default EditUser;
